import { apiGet, apiDelete, getApiClient, getBaseUrl, getAuthCredentials } from './api';
import type { Attachment, HalCollection } from '@/types';

// Obținere atașamente pentru un work package
export async function getWorkPackageAttachments(
  workPackageId: number
): Promise<Attachment[]> {
  const response = await apiGet<HalCollection<Attachment>>(
    `/work_packages/${workPackageId}/attachments`
  );

  return response._embedded.elements;
}

// Upload atașament pentru work package
export async function uploadWorkPackageAttachment(
  workPackageId: number,
  file: File,
  description?: string
): Promise<Attachment> {
  const formData = new FormData();
  formData.append('file', file);

  if (description) {
    const metadata = JSON.stringify({
      description: { raw: description },
      fileName: file.name,
    });
    formData.append('metadata', metadata);
  }

  // Use auth/data endpoint for upload
  const credentials = getAuthCredentials();
  const response = await fetch(`/auth/data/work-packages/${workPackageId}/attachments`, {
    method: 'POST',
    headers: {
      'Authorization': `Basic ${credentials}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw { message: error.message || 'Upload failed', status: response.status };
  }

  return response.json();
}

// Upload atașament pentru proiect (Wiki, etc.)
export async function uploadProjectAttachment(
  projectId: number,
  file: File,
  description?: string
): Promise<Attachment> {
  const client = getApiClient();

  const formData = new FormData();
  formData.append('file', file);

  if (description) {
    const metadata = JSON.stringify({
      description: { raw: description },
      fileName: file.name,
    });
    formData.append('metadata', metadata);
  }

  const response = await client.post<Attachment>(
    `/projects/${projectId}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
}

// Ștergere atașament
export async function deleteAttachment(attachmentId: number, reason?: string): Promise<void> {
  await apiDelete(`/auth/data/attachments/${attachmentId}`, {
    data: reason ? { reason } : undefined
  });
}

// Obținere URL pentru download
export function getAttachmentDownloadUrl(attachment: Attachment): string {
  if (attachment._links.downloadLocation?.href) {
    const href = attachment._links.downloadLocation.href;

    // If it's our auth server endpoint, use it directly
    if (href.startsWith('/auth/files/')) {
      return href;
    }

    // Dacă este URL relativ, adăugăm base URL-ul
    if (href.startsWith('/')) {
      return `${getBaseUrl()}${href}`;
    }

    return href;
  }

  // Fallback la self link
  return `${getBaseUrl()}/api/v3/attachments/${attachment.id}/content`;
}

// Download atașament (pentru cazuri când trebuie să gestionăm autentificarea)
export async function downloadAttachment(attachment: Attachment): Promise<Blob> {
  const client = getApiClient();
  const url = attachment._links.downloadLocation?.href || `/attachments/${attachment.id}/content`;

  const response = await client.get(url, {
    responseType: 'blob',
  });

  return response.data;
}

// Helper pentru a formata dimensiunea fișierului
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Helper pentru a obține iconița în funcție de tipul fișierului
export function getFileIcon(contentType: string): string {
  const type = contentType.toLowerCase();

  if (type.includes('image')) return 'image';
  if (type.includes('pdf')) return 'file-text';
  if (type.includes('word') || type.includes('document')) return 'file-text';
  if (type.includes('excel') || type.includes('spreadsheet')) return 'table';
  if (type.includes('powerpoint') || type.includes('presentation')) return 'presentation';
  if (type.includes('zip') || type.includes('archive') || type.includes('compressed')) return 'archive';
  if (type.includes('video')) return 'video';
  if (type.includes('audio')) return 'audio';
  if (type.includes('text')) return 'file-text';
  if (type.includes('code') || type.includes('javascript') || type.includes('json')) return 'code';

  return 'file';
}

// Helper pentru a verifica dacă fișierul este o imagine
export function isImageFile(contentType: string): boolean {
  return contentType.toLowerCase().startsWith('image/');
}

// Helper pentru a verifica dacă fișierul poate fi previzualizat
export function canPreviewFile(contentType: string): boolean {
  const type = contentType.toLowerCase();
  return (
    type.startsWith('image/') ||
    type === 'application/pdf' ||
    type.startsWith('text/')
  );
}

// Download fișier individual - folosește link direct pentru fișiere mari
export async function downloadAttachmentFile(attachmentId: number, fileName: string): Promise<void> {
  // Folosim endpoint-ul direct care nu necesită autentificare în header
  // și permite browser-ului să gestioneze descărcarea nativ
  const sanitizedFileName = fileName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '_');
  const url = `/auth/files/${attachmentId}/${encodeURIComponent(sanitizedFileName)}`;

  // Deschidem într-un iframe ascuns pentru a forța descărcarea
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);

  // Curățăm iframe-ul după 60 secunde
  setTimeout(() => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, 60000);
}

// Download multiple fișiere ca ZIP - folosește form submission pentru fișiere mari
export async function downloadAttachmentsAsZip(attachmentIds: number[], archiveName: string): Promise<void> {
  const credentials = getAuthCredentials();

  if (!credentials) {
    throw new Error('Nu sunteți autentificat. Reîncărcați pagina și încercați din nou.');
  }

  // Folosim form submission pentru a evita probleme cu memoria la fișiere mari
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = '/auth/data/attachments/download-zip';
  form.style.display = 'none';

  // Adăugăm câmpurile necesare
  const authField = document.createElement('input');
  authField.type = 'hidden';
  authField.name = 'authorization';
  authField.value = credentials;
  form.appendChild(authField);

  const idsField = document.createElement('input');
  idsField.type = 'hidden';
  idsField.name = 'attachmentIds';
  idsField.value = JSON.stringify(attachmentIds);
  form.appendChild(idsField);

  const nameField = document.createElement('input');
  nameField.type = 'hidden';
  nameField.name = 'archiveName';
  nameField.value = archiveName;
  form.appendChild(nameField);

  // Submitem form-ul
  document.body.appendChild(form);
  form.submit();

  // Curățăm form-ul
  setTimeout(() => {
    if (form.parentNode) {
      form.parentNode.removeChild(form);
    }
  }, 1000);
}
